import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EllipsisTooltipDirective } from '../../shared/ellipsis-tooltip.directive';
import { MatDialog } from '@angular/material/dialog';
import { AlertCustomComponent } from 'pharedata-ng-components';
import { GlobalState } from '../../services/global-state';
import {
  EditDialogComponent,
  EditDialogData,
} from './edit-dialog/edit-dialog.component';
import { ParentMessagesSender } from '../../services/parent-messages-sender';
import { LlamanosApiService } from '../../services/api-llamanos.service';

@Component({
  selector: 'app-mantenedor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    EllipsisTooltipDirective,
    AlertCustomComponent,
  ],
  templateUrl: './mantenedor.component.html',
  styleUrl: './mantenedor.component.scss',
})
export class MantenedorComponent {
  isRowArrowOpen: boolean = false;
  categorias: any = [];
  openedCategoria: any = {};
  editingSubcategoria: any = {};
  showAlert: boolean = false;
  alertTitle = '';
  alertContent = '';
  alertIcon = '';
  isReloading: boolean = false;
  pendingOperations: number = 0;
  searchText: string = '';

  constructor(
    private globalState: GlobalState,
    private parentMessagesSender: ParentMessagesSender,
    private dialog: MatDialog,
    private apiLlamanosService: LlamanosApiService,
  ) {}

  ngOnInit(): void {
    this.apiLlamanosService.getCategorias().subscribe({
      next: (data) => {
        const source = data || {};
        const entries = Object.entries(source);
        const previousOpenedKey = this.openedCategoria?.key;

        this.categorias = entries.map(([key, value]) => {
          const rawSubcats: any[] = Array.isArray(value) ? value : [];
          const normalizedSubcats: any[] = rawSubcats.map((sub) => {
            const pairs = Object.entries(sub || {});
            const [subKey, subVal] = pairs.length > 0 ? pairs[0] : ['', {}];
            if (subVal && typeof subVal === 'object') {
              return { key: subKey, ...subVal };
            }
            return { key: subKey, value: subVal };
          });
          const active = normalizedSubcats.some(
            (s: any) => s && s.active === true,
          );
          return { key, subcategorias: normalizedSubcats, active };
        });

        // Restore openedCategoria reference after data reload
        if (previousOpenedKey) {
          const updatedOpenedCategoria = this.categorias.find(
            (c: any) => c && c.key === previousOpenedKey,
          );
          if (updatedOpenedCategoria) {
            this.openedCategoria = updatedOpenedCategoria;
          }
        }
      },
      error: (err) => {
        console.error('Error cargando categorias:: ', err);
      },
    });
  }

  // Normalize text for search (remove accents and convert to lowercase)
  private normalizeText(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  // Filter categories based on search text
  get filteredCategorias(): any[] {
    if (!this.searchText || this.searchText.trim() === '') {
      return this.categorias;
    }

    const searchNormalized = this.normalizeText(this.searchText.trim());

    return this.categorias.filter((categoria: any) => {
      // Check if category name matches
      const categoriaNameMatches = this.normalizeText(
        categoria.key || '',
      ).includes(searchNormalized);

      // Check if any subcategory name matches
      const hasMatchingSubcategoria = (categoria.subcategorias || []).some(
        (subcat: any) =>
          this.normalizeText(subcat.key || '').includes(searchNormalized),
      );

      return categoriaNameMatches || hasMatchingSubcategoria;
    });
  }

  toggleRowArrow(entry: any): void {
    if (this.openedCategoria && this.openedCategoria.key == entry.key)
      this.openedCategoria = {};
    else this.openedCategoria = entry;
  }

  toggleSubcategoriaSwitch(subcat: any): void {
    if (!subcat) return;

    // Toggle subcategory active state
    const newActive = !subcat.active;
    subcat.active = newActive;

    // Locate parent category (prefer the currently opened one for speed)
    let parent: any = null;
    if (
      this.openedCategoria &&
      Array.isArray(this.openedCategoria.subcategorias) &&
      this.openedCategoria.subcategorias.some(
        (s: any) => s && s.key === subcat.key,
      )
    ) {
      parent = this.openedCategoria;
    }
    if (!parent) {
      parent = this.categorias.find(
        (c: any) =>
          c &&
          Array.isArray(c.subcategorias) &&
          c.subcategorias.some((s: any) => s && s.key === subcat.key),
      );
    }

    if (parent) {
      // Parent is active if any subcategory is active
      const anyActive =
        parent.subcategorias?.some((s: any) => s && s.active === true) ?? false;
      parent.active = anyActive;

      // Mirror into categorias array (source of truth)
      const catIdx = this.categorias.findIndex(
        (c: any) => c && c.key === parent.key,
      );
      if (catIdx !== -1) {
        this.categorias[catIdx].active = anyActive;
        if (Array.isArray(this.categorias[catIdx].subcategorias)) {
          const subIdx = this.categorias[catIdx].subcategorias.findIndex(
            (s: any) => s && s.key === subcat.key,
          );
          if (subIdx !== -1) {
            this.categorias[catIdx].subcategorias[subIdx].active = newActive;
          }
        }

        // Keep openedCategoria referencing the same object as in categorias
        if (this.openedCategoria && this.openedCategoria.key === parent.key) {
          this.openedCategoria = this.categorias[catIdx];
        }
      }
    }

    // Prepare and send change for this subcategory
    const categoriaKey = parent?.key ?? this.openedCategoria?.key ?? '';
    const subcategoriaKey = subcat?.key ?? '';
    if (categoriaKey && subcategoriaKey) {
      const body = [
        {
          categoria: categoriaKey,
          subcategoria: subcategoriaKey,
          active: newActive,
        },
      ];
      this.apiLlamanosService.updateCategorias(body).subscribe({
        next: () => {},
        error: (err) => console.error('Error actualizando subcategoria:', err),
      });
    }
  }

  toggleCategoriaSwitch(cat: any): void {
    if (!cat) return;
    const newActive = !cat.active;

    // Update the passed-in category
    cat.active = newActive;
    if (Array.isArray(cat.subcategorias)) {
      cat.subcategorias.forEach((sub: any) => {
        if (sub) sub.active = newActive;
      });
    }

    // Mirror the update inside the categorias list (source of truth)
    const idx = this.categorias.findIndex((c: any) => c && c.key === cat.key);
    if (idx !== -1) {
      this.categorias[idx].active = newActive;
      if (Array.isArray(this.categorias[idx].subcategorias)) {
        this.categorias[idx].subcategorias.forEach((sub: any) => {
          if (sub) sub.active = newActive;
        });
      }

      // Ensure openedCategoria keeps the same object reference
      if (this.openedCategoria && this.openedCategoria.key === cat.key) {
        this.openedCategoria = this.categorias[idx];
      }
    }

    // Send changes for all subcategories under this category
    const subs = Array.isArray(cat.subcategorias) ? cat.subcategorias : [];
    const body = subs
      .filter((s: any) => !!s && typeof s.key === 'string')
      .map((s: any) => ({
        categoria: cat.key,
        subcategoria: s.key,
        active: newActive,
      }));
    if (body.length > 0) {
      this.apiLlamanosService.updateCategorias(body).subscribe({
        next: () => {},
        error: (err) => console.error('Error actualizando categoria:', err),
      });
    }
  }

  openEditDialog(subcat: any): void {
    // Prevent opening dialog if a reload is in progress
    if (this.isReloading) {
      console.log('Recarga en progreso, esperando...');
      return;
    }

    // Always get fresh data from openedCategoria instead of using cached editingSubcategoria
    const freshSubcat = this.openedCategoria?.subcategorias?.find(
      (s: any) => s && s.key === subcat.key,
    );

    if (!freshSubcat) {
      console.error('No se encontró la subcategoría en los datos actuales');
      return;
    }

    // Create a deep copy to avoid reference issues
    this.editingSubcategoria = JSON.parse(JSON.stringify(freshSubcat));

    const dialogData: EditDialogData = {
      openedCategoriaKey: this.openedCategoria?.key ?? '',
      editingSubcategoria: this.editingSubcategoria,
    };

    const innerHeight = window.innerHeight;
    let scrollY = this.globalState.scrollY;

    if (scrollY + 800 > innerHeight) {
      console.log('scroll muy abajo, subiendo');
      scrollY = scrollY - 500;
    }

    const dialogRef = this.dialog.open(EditDialogComponent, {
      width: '360px',
      data: dialogData,
      disableClose: false,
      backdropClass: 'custom-backdrop',
      position: {
        top: `${scrollY}px`,
        left: '0px',
      },
    });
    this.parentMessagesSender.showGreyBackground();

    dialogRef.afterClosed().subscribe((result) => {
      this.parentMessagesSender.hideGreyBackground();

      // Clean up editingSubcategoria when dialog closes
      this.editingSubcategoria = {};

      if (result && result.sortChanges) {
        const sortBody = result.sortChanges as Array<{
          categoria: string;
          subcategoria: string;
          fields: Array<{ field_name: string; sort: number }>;
        }>;

        // Show loader manually and track operations
        this.pendingOperations = 2; // updateCategoriasSort + getCategorias
        this.parentMessagesSender.displayLoader();

        this.apiLlamanosService.updateCategoriasSort(sortBody).subscribe({
          next: () => {
            this.showAlert = true;
            this.alertIcon = 'success';
            this.alertTitle = '¡Operación exitosa!';
            this.alertContent =
              'Cambios en la gestión de campos guardados con éxito.';

            // First operation done, decrease counter
            this.pendingOperations--;

            // Keep loader visible if there are pending operations
            if (this.pendingOperations > 0) {
              this.parentMessagesSender.displayLoader();
            }

            // Reload will handle the second operation
            this.reloadCategoriasAndUpdateReferences();
          },
          error: (err) => {
            console.error('Error actualizando sort:', err);
            this.pendingOperations = 0;
            this.parentMessagesSender.hideLoader();
          },
        });
      } else if (result && result.fieldsRelationChanges) {
        const relationBody = result.fieldsRelationChanges as Array<{
          categoria: string;
          subcategoria: string;
          fields: Array<any>;
        }>;

        // Check if any field contains "subcampo" property
        const hasSubcampo = relationBody.some((item) =>
          item.fields?.some((field: any) => field.subcampo !== undefined),
        );

        const alertMessage = hasSubcampo
          ? 'Subcampo añadido con éxito.'
          : 'Cambios en la gestión de campos guardados con éxito.';

        // Show loader manually and track operations
        this.pendingOperations = 2; // updateCategoriasSort + getCategorias
        this.parentMessagesSender.displayLoader();

        this.apiLlamanosService.updateCategoriasSort(relationBody).subscribe({
          next: () => {
            this.showAlert = true;
            this.alertIcon = 'success';
            this.alertTitle = '¡Operación exitosa!';
            this.alertContent = alertMessage;

            // First operation done, decrease counter
            this.pendingOperations--;

            // Keep loader visible if there are pending operations
            if (this.pendingOperations > 0) {
              this.parentMessagesSender.displayLoader();
            }

            // Reload will handle the second operation
            this.reloadCategoriasAndUpdateReferences();
          },
          error: (err) => {
            console.error('Error actualizando relación de campos:', err);
            this.pendingOperations = 0;
            this.parentMessagesSender.hideLoader();
          },
        });
      } else if (result) {
        console.log('Usuario confirmó la edición');
      } else {
        console.log('Usuario canceló la edición');
      }
    });
  }

  reloadCategoriasAndUpdateReferences(): void {
    const openedCategoriaKey = this.openedCategoria?.key;

    this.isReloading = true;
    console.log('Iniciando recarga de categorías...');

    this.apiLlamanosService.getCategorias().subscribe({
      next: (data) => {
        const source = data || {};
        const entries = Object.entries(source);

        this.categorias = entries.map(([key, value]) => {
          const rawSubcats: any[] = Array.isArray(value) ? value : [];
          const normalizedSubcats: any[] = rawSubcats.map((sub) => {
            const pairs = Object.entries(sub || {});
            const [subKey, subVal] = pairs.length > 0 ? pairs[0] : ['', {}];
            if (subVal && typeof subVal === 'object') {
              return { key: subKey, ...subVal };
            }
            return { key: subKey, value: subVal };
          });
          const active = normalizedSubcats.some(
            (s: any) => s && s.active === true,
          );
          return { key, subcategorias: normalizedSubcats, active };
        });

        // Update openedCategoria reference with fresh data
        if (openedCategoriaKey) {
          const updatedOpenedCategoria = this.categorias.find(
            (c: any) => c && c.key === openedCategoriaKey,
          );
          if (updatedOpenedCategoria) {
            this.openedCategoria = updatedOpenedCategoria;
          }
        }

        console.log('Categorías actualizadas con datos frescos del servidor');
        this.isReloading = false;

        // Second operation done, decrease counter
        this.pendingOperations--;

        // Only hide the loader when ALL operations are complete
        if (this.pendingOperations <= 0) {
          this.pendingOperations = 0;
          this.parentMessagesSender.hideLoader();
          console.log('Todas las operaciones completadas, ocultando loader');
        } else {
          // Ensure loader is still visible
          this.parentMessagesSender.displayLoader();
          console.log(
            `Aún quedan ${this.pendingOperations} operaciones pendientes`,
          );
        }
      },
      error: (err) => {
        console.error('Error cargando categorias:: ', err);
        this.isReloading = false;
        this.pendingOperations = 0;

        // Hide the loader on error
        this.parentMessagesSender.hideLoader();
      },
    });
  }

  recargarCampos(): void {
    this.apiLlamanosService.syncCategoriesSchemas().subscribe({
      next: () => {
        this.alertTitle = 'Recarga Iniciada';
        this.alertContent =
          'La recarga de campos se ha iniciado exitosamente. En unos momentos estos ya estarán disponibles.';
        this.alertIcon = 'success';
        this.showAlert = true;
        this.ngOnInit();
      },
      error: (err) => {
        this.alertTitle = 'Error';
        this.alertContent = `Ha ocurrido un error: ${err}`;
        this.alertIcon = 'error';
        this.showAlert = true;
        console.error('Error sincronizando categorías:', err);
      },
    });
  }

  closeAlert = () => {
    this.showAlert = false;
  };

  hideGreyBackground = () => {
    this.parentMessagesSender.hideGreyBackground();
  };

  showGreyBackground = () => {
    this.parentMessagesSender.showGreyBackground();
  };

  scrollTo = (x: number, y: number) => {
    this.parentMessagesSender.scrollTo(x, y);
  };
}
